
import React from 'react';
import { Circle, Text } from 'react-konva';
import { Rhombus, CLOCKWISE, COUNTER_CLOCKWISE, LINE_SIZE, ROT_DEGREES,
         ORDERED_ANGLE_TO_RVX_INDICES, RHOMBUS_ANGLES } from './Rhombus.js';
import { Globals } from './Globals.js';
var Point = require('./Point');

function assert( expression, text) {
  if (!expression) {
    throw new Error(text);
  }
}

class VertexCandidates {
  center = null;
  existing_rhombuses = null;
  candidates = null;
  total_angle = -1.0;
  save_room_for = null;
  is_initialized = false;

  constructor( center, rhombus_list) {
    this.center = center;
    this.existing_rhombuses= rhombus_list;
    this.candidates = [];
    this.total_angle = -1.0;
    this.save_room_for = new Object();
    this.save_room_for[CLOCKWISE] = 0.0;
    this.save_room_for[COUNTER_CLOCKWISE] = 0.0;
    this.is_initialized = false;
  }

  initialize( calculate_room_to_save=true) {

    if (this.existing_rhombuses.is_empty()) {
      // this is straight-forward
      for (let a=0.0; a<360.0; a+=ROT_DEGREES) {
        let v = new Point(LINE_SIZE, 0.0);
        v.rotate(a);
        v.add(this.center);
        this.candidates.push(v);
      }
      this.total_angle = 360.0;
      this.is_initialized = true;
      return true;
    }

    // sweep clockwise through all the angles starting at 0-degrees making
    // a list of indicating which are blocked or not
    let angle_blocked = [];
    let vectors = [];
    let count_blocked = 0;
    for (let a=0.0; a<360.0; a+=ROT_DEGREES) {
      let v = new Point(LINE_SIZE, 0.0);
      v.rotate(a);
      let block_status = false;
      if (this.existing_rhombuses.is_vertex_blocked(v, this.center)) {
        block_status = true;
        count_blocked += 1;
      } else {
        // is_vertex_blocked returns false if this angle is
        // external. Check half-way between the angle to see if it
        // is blocked.
        let tv = Point.fromObject(v).rotate(ROT_DEGREES/2.0);
        if (this.existing_rhombuses.is_vertex_blocked(tv, this.center)) {
          block_status = true;
          count_blocked += 1;
        }
      }
      angle_blocked.push(block_status);
      vectors.push(v);
    }

    if ( angle_blocked.length <= count_blocked ) {
      // all angles are blocked, no candidate vertices available
      return false;
    }

    assert( count_blocked > 0, "No angles blocked");
    assert( angle_blocked.length === vectors.length);

    // find the first unblocked angle in the clockwise direction and
    // set start_index to that value
    let start_index = -1;
    if (angle_blocked[0]) {
      // rotate clockwise to find the first unblocked angle
      for( let abidx=0; abidx<angle_blocked.length; abidx++) {
        if (!angle_blocked[abidx]) {
          start_index = abidx;
          break;
        }
      }
    } else {
      // rotate counter-clockwise to find the first blocked angle
      for( let abidx=angle_blocked.length-1; abidx>-1; abidx--) {
        if (angle_blocked[abidx]) {
          start_index = abidx + 1;
          break;
        }
      }
    }

    assert( start_index > -1, `No unblocked angles found in ${angle_blocked}`);

    // Starting at the start_index, create a list of candidate vertices
    let idx = start_index % angle_blocked.length;
    this.total_angle = 0.0;
    while (!angle_blocked[idx]) {
      this.candidates.push(Point.fromObject(vectors[idx]).add(this.center));
      idx = (idx+1)%angle_blocked.length;
      this.total_angle += ROT_DEGREES;
      assert( this.total_angle < 360.0, `Infinite loop: ${this.total_angle} ${idx}`);
    }
    this.candidates.push(Point.fromObject(vectors[idx]).add(this.center));

    assert(this.candidates.length, "Need at least 2 vertex candidates");

    // we also need to check if there are restrictions on what
    // type of rhombus we can put at the ends of the fill direction
    if (calculate_room_to_save) {
      for( let direction in this.save_room_for) {
        let oeaa = this.get_other_end_available_angle(direction);
        assert( !(oeaa < ROT_DEGREES), `Angle too small: ${oeaa}`);
        if (oeaa < ORDERED_ANGLE_TO_RVX_INDICES[0]) {
          // we are going to have to save room at the other end, so let's
          // reduce the total_angle by the size of what's available
          this.save_room_for[direction] = 180.0 - oeaa;
        }
      }
    }

    // Make sure that the intermediate candidates are not external vertices
    // first and last candidates are always external
    for( let v=1; v<(this.candidates.length-1); v++) {
      if (this.existing_rhombuses.is_external_vertex(this.candidates[v])) {
        return false;
      }
    }

    this.is_initialized = true;
    return true;
  }

  get_size() {
    return this.candidates.length;
  }

  get_vertex_at( index) {
    assert( (index >= 0) && (index < this.candidates.length), 
            `index=${index} out of range of ${this.candidates.length}`);
    return this.candidates[index].clone();
  }

  get_other_end_available_angle( direction) {
    let cvidx = (direction==COUNTER_CLOCKWISE) ? 0 : (this.candidates.length-1);
    let oe_vertex = this.candidates[cvidx];
    // let's just use this class to create a candidate list at the other end
    let cl = new VertexCandidates( oe_vertex, this.existing_rhombuses);
    cl.initialize(false);
    return cl.get_remaining_angle();
  }

  get_remaining_angle() {
    return this.total_angle;
  }

  generate_rhombus() {
    // make sure we save room for a rhombus that will fit at the other end
    let globals = new Globals();
    let save_angle = this.save_room_for[globals.direction];
    if (this.total_angle == save_angle) {
      // we don't need to save it anymore, time to fill it
      save_angle = 0.0;
    }
    if (this.total_angle < save_angle) {
      globals.status_message = Globals.SM_STOPPED_DUE_TO_OVERLAP;
      return null;
    }
    let rhombus_vertex = Rhombus.suggested_rhombus_vertex( this.total_angle - save_angle);
    let cvidx = globals.direction == CLOCKWISE ? 0 : (this.get_size()-1);
    let r = new Rhombus({vertex_id:rhombus_vertex, position:this.center, alignment:this.get_vertex_at(cvidx)});

    // check if the new rhombus overlaps with any existing ones
    if (this.existing_rhombuses.does_rhombus_overlap(r)) {
      globals.status_message = Globals.SM_STOPPED_DUE_TO_OVERLAP;
      return null;
    }

    if (!this.existing_rhombuses.are_external_vertices_adjacent(r)) {
      // console.log('HOLE found in generate_rhombus');
      globals.status_message = Globals.SM_STOPPED_DUE_TO_HOLE;
      return null;
    }
    
    // remove the vertices that are no longer in play, that is the
    // first set of vertices within the angle of the rhombus vertex
    for (let i=0; i<Math.floor(RHOMBUS_ANGLES[rhombus_vertex]/ROT_DEGREES); i++) {
      if (globals.direction == CLOCKWISE) {
        this.candidates.shift();
      } else {
        this.candidates.pop();
      }
    }

    // update the remaining angle-space left
    this.total_angle -= RHOMBUS_ANGLES[rhombus_vertex];

    if (this.total_angle < ROT_DEGREES) {
      // get rid of remaining candidates
      this.candidates.splice(0,this.candidates.length);
    }

    return r;
  }

  render() {

    return (
      <>
        { this.candidates.map((cv,idx) => {
          return (
            <React.Fragment key={"fc"+idx}>
              <Circle key={"c"+idx} x={cv.x} y={cv.y} radius={10} fill="blue"/>
              <Text key={"t"+idx} text={idx} x={cv.x-2} y={cv.y-3} fontSize={8} strokeWidth={1} stroke="white"/>
            </React.Fragment>
          )
        })}
      </>
      )
  }
  
}

export { VertexCandidates };
